import axios, { type AxiosError } from 'axios'
import type { ApiResponse } from '../../types'

// Single axios instance for the Circle of Trust community API. Every community
// module (me, persons, circles, groups, friends, rooms, safety) shares it.
// Base URL: `${VITE_API_URL}/api/v1/community` — so callers pass paths like `/me`.
const COMMUNITY_BASE = (import.meta.env.VITE_API_URL || '') + '/api/v1/community'

export const client = axios.create({
  baseURL: COMMUNITY_BASE,
  headers: { 'Content-Type': 'application/json' },
})

// Bearer from the same session slot AuthContext writes.
client.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('cot_jwt_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// 401 → drop the session and bounce to /login (mirrors the auth client).
client.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    // A delayed response from a previous account cannot log out the current account.
    const authorization = err.config?.headers?.Authorization
    const sentToken = typeof authorization === 'string' && authorization.startsWith('Bearer ') ? authorization.slice(7) : null
    if (err.response?.status === 401 && sentToken === sessionStorage.getItem('cot_jwt_token')) {
      sessionStorage.removeItem('cot_jwt_token')
      sessionStorage.removeItem('cot_user')
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(err)
  }
)

/** Error carrying the HTTP status so callers can branch (e.g. 404 → onboarding). */
export class CotApiError extends Error {
  status?: number
  constructor(message: string, status?: number) {
    super(message)
    this.name = 'CotApiError'
    this.status = status
  }
}

/** Normalise any thrown value into a CotApiError, reading `errors[0] ?? message`. */
export function apiError(err: unknown): CotApiError {
  if (err instanceof CotApiError) return err
  const axErr = err as AxiosError<ApiResponse>
  const body = axErr?.response?.data
  const message =
    body?.errors?.[0] ??
    body?.message ??
    (err as Error)?.message ??
    'Something went wrong. Please try again.'
  return new CotApiError(message, axErr?.response?.status)
}

/** Unwrap an ApiResponse<T> envelope, throwing on `success:false`. */
export function unwrap<T>(envelope: ApiResponse<T>): T {
  if (envelope && envelope.success === false) {
    throw new CotApiError(envelope.errors?.[0] ?? envelope.message ?? 'Request failed.')
  }
  return (envelope?.data ?? null) as T
}

/**
 * Foundation scaffolds every Wave-2 api-module function as a typed stub calling
 * this. Wave-2 replaces the body with a real request. The rest args let a stub
 * "consume" its parameters so the correct signature stands under strict TS.
 */
export function notImplemented(fn: string, ..._args: unknown[]): never {
  throw new Error('not implemented: ' + fn)
}
